/**
 * Calendar boundaries in Danish time. Budgets reset on the first of the month and the usage page
 * says "today", both as a Dane sees them (Europe/Copenhagen, daylight saving included).
 */

export const BILLING_TIME_ZONE = 'Europe/Copenhagen'

const formatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: BILLING_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hourCycle: 'h23',
})

interface LocalParts {
  year: number
  month: number
  day: number
  hour: number
  minute: number
  second: number
}

function localParts(date: Date): LocalParts {
  const parts: Record<string, number> = {}
  for (const part of formatter.formatToParts(date)) {
    if (part.type !== 'literal') parts[part.type] = Number(part.value)
  }
  return {
    year: parts.year ?? 0,
    month: parts.month ?? 0,
    day: parts.day ?? 0,
    hour: parts.hour ?? 0,
    minute: parts.minute ?? 0,
    second: parts.second ?? 0,
  }
}

/** The offset (ms) Copenhagen is ahead of UTC at `date`. */
function offsetAt(date: Date): number {
  const p = localParts(date)
  const asUtc = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second)
  return asUtc - Math.floor(date.getTime() / 1000) * 1000
}

/** The instant a Copenhagen wall-clock time happens (midnight is never skipped by daylight saving there). */
function instantOf(year: number, month: number, day: number): Date {
  const guess = Date.UTC(year, month - 1, day)
  // Two passes settle the offset even right at a change of clocks
  const first = guess - offsetAt(new Date(guess))
  return new Date(guess - offsetAt(new Date(first)))
}

/** Midnight at the start of the Copenhagen day that contains `date`. */
export function startOfDay(date: Date, daysBack = 0): Date {
  const p = localParts(date)
  // Date.UTC carries a day below 1 into the previous month
  return instantOf(p.year, p.month, p.day - daysBack)
}

/** Midnight at the start of the Copenhagen month that contains `date`. */
export function startOfMonth(date: Date): Date {
  const p = localParts(date)
  return instantOf(p.year, p.month, 1)
}

/** The first day of the next Copenhagen month. */
export function startOfNextMonth(date: Date): Date {
  const p = localParts(date)
  return p.month === 12 ? instantOf(p.year + 1, 1, 1) : instantOf(p.year, p.month + 1, 1)
}

/** `2026-10`, the key of the month `date` falls in (Copenhagen time). */
export function monthKey(date: Date): string {
  const p = localParts(date)
  return `${p.year}-${String(p.month).padStart(2, '0')}`
}

/** The Copenhagen calendar date of `date` as `YYYY-MM-DD`. */
export function dateKey(date: Date): string {
  const p = localParts(date)
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`
}

export const USAGE_PERIODS = ['today', 'week', 'month', 'total'] as const
export type UsagePeriod = (typeof USAGE_PERIODS)[number]

/** Where each usage window starts. `total` has no start. The week is the last seven days. */
export function usageWindows(now: Date): Record<UsagePeriod, Date | null> {
  return {
    today: startOfDay(now),
    week: startOfDay(now, 6),
    month: startOfMonth(now),
    total: null,
  }
}
